import React, { FC, useMemo, useState } from 'react';
import Image from 'components/commercetools-ui/atoms/image';
import Link from 'components/commercetools-ui/atoms/link';
import OutOfStock from 'components/commercetools-ui/atoms/out-of-stock';
import Typography from 'components/commercetools-ui/atoms/typography';
import QuickAdd from 'components/commercetools-ui/organisms/product/product-list/components/product-quick-add';
import WishlistButton from 'components/commercetools-ui/organisms/wishlist/components/wishlist-button';
import BundleIcon from 'components/icons/bundle-icon';
import EbookIcon from 'components/icons/ebook-icon';
import PhysicalBookIcon from 'components/icons/physical-book-icon';
import useMediaQuery from 'helpers/hooks/useMediaQuery';
import useVariantWithDiscount from 'helpers/hooks/useVariantWithDiscount';
import { getDiscount } from 'helpers/utils/getDiscount';
import { desktop } from 'helpers/utils/screensizes';
import { Product, Variant } from 'types/entity/product';
import { LineItem, Wishlist } from 'types/entity/wishlist';
import { AddItemResponse } from 'frontastic/hooks/useCart/types';
import Prices from './prices';
import useTrack from './useTrack';
import DiscountBadge from '../badge/DiscountBadge';

export interface ProductTileProps {
  product: Product;
  isSearchResult?: boolean;
  disableWishlistButton?: boolean;
  selectedVariantIndex?: number;
  wishlist?: Wishlist;
  onClick?: () => void;
  removeLineItem?: (item: LineItem) => Promise<void>;
  addToWishlist?: (lineItem: LineItem, count: number) => Promise<void>;
  onAddToCart?: (variant: Variant, quantity: number) => Promise<AddItemResponse>;
  className?: string;
}

const ProductTile: FC<ProductTileProps> = ({
  className,
  product,
  isSearchResult = false,
  disableWishlistButton = false,
  selectedVariantIndex = 0,
  wishlist,
  onClick,
  addToWishlist,
  removeLineItem,
  onAddToCart,
}) => {
  const [isDesktopSize] = useMediaQuery(desktop);

  const { ref } = useTrack({ product });

  const variantWithDiscount = useVariantWithDiscount(product.variants) as Variant;

  const [userSelectedVariant, setUserSelectedVariant] = useState<Variant | undefined>(undefined);

  const selectedVariant = userSelectedVariant ?? variantWithDiscount ?? product?.variants[selectedVariantIndex];

  const { hasDiscount, discountedPrice, discountPercentage } = getDiscount(selectedVariant ?? ({} as Variant));

  const productToWishlistLineItem = useMemo<LineItem | undefined>(() => {
    if (product) {
      return {
        lineItemId: product.productId ?? '',
        productId: product.productId,
        productKey: product.productKey,
        productRef: product.productRef,
        name: product.name,
        count: 1,
        variant: selectedVariant,
        addedAt: new Date(),
        _url: product._url,
      };
    }
  }, [product, selectedVariant]);

  const productUrl = useMemo(() => {
    if (!product._url) return `/${(product.name ?? '').toLowerCase().replace(/\s+/g, '-')}/p/${selectedVariant.sku}`;

    const name = product._url.split('/')[1];

    return `/${name}/p/${selectedVariant.sku}` + (isSearchResult ? '?sr=1' : '');
  }, [product, selectedVariant, isSearchResult]);

  return (
    <div
      onClick={onClick}
      ref={ref}
      className={`${className} px-15 py-20 hover:bg-gray-200 focus:bg-gray-200 active:bg-gray-200`}
    >
      <div className="relative">
        <Link link={productUrl}>
          <div className="relative w-full" data-testid="image-container">
            <div className="relative bg-transparent">
              <div className="relative block w-full pt-[100%]">
                {selectedVariant.images?.[0] ? (
                  <Image
                    src={selectedVariant.images?.[0] || ''}
                    suffix="medium"
                    alt={product.name ?? ''}
                    style={{ objectFit: 'contain', objectPosition: 'top' }}
                    className="w-full rounded-sm group-hover:opacity-75"
                    fill
                  />
                ) : (
                  <Image
                    className="absolute inset-0 size-full rounded-sm object-contain object-[center_top] text-transparent group-hover:opacity-75"
                    src="/images/imagen.png"
                    alt="Product coming soon image in cart"
                  />
                )}
              </div>
            </div>
          </div>
        </Link>
        <span
          className="absolute -right-10 top-0 z-10 flex size-15 cursor-pointer items-center justify-center md:size-20"
          onClick={(e) => e.preventDefault()}
        >
          {!disableWishlistButton && productToWishlistLineItem && (
            <WishlistButton
              data={wishlist}
              lineItem={productToWishlistLineItem}
              className="size-full"
              addToWishlist={addToWishlist}
              removeFromWishlist={removeLineItem}
            />
          )}
        </span>
        <div className="absolute -left-10 top-0 z-10 w-full">
          <div className="w-full text-center">
            {hasDiscount && <DiscountBadge discountPercentage={discountPercentage} />}
          </div>
          {/* <div className="w-full text-center">
            {!selectedVariant.isOnStock && (
              <div className="mb-8">
                <OutOfStock />
              </div>
            )}
          </div> */}
        </div>
      </div>

      <div className="group">
        <div className="flex h-fit flex-col justify-between overflow-hidden sm:h-165 md:h-175 lg:h-185">
          <Link link={productUrl}>
            <div className="mt-6 line-clamp-2 overflow-hidden text-ellipsis text-lg font-bold leading-loose text-blue-900 underline-offset-4 group-hover:underline group-focus:underline group-active:underline md:mt-12 lg:min-h-45">
              {product?.name}
            </div>

            <div className="mt-6 line-clamp-2 max-h-38 overflow-hidden align-top text-sm leading-normal">
              {(selectedVariant?.attributes?.['publicationYear'] ||
                selectedVariant?.attributes?.['publicationMonth'] ||
                selectedVariant?.attributes?.['publicationDay'] ||
                selectedVariant?.attributes?.['author']) && (
                <>
                  {(selectedVariant?.attributes?.['publicationYear'] ||
                    selectedVariant?.attributes?.['publicationMonth'] ||
                    selectedVariant?.attributes?.['publicationDay']) && (
                    <Typography
                      as="span"
                      className={`mr-6 inline w-fit border-gray-600 pr-6 align-top text-gray-600 ${selectedVariant?.attributes?.['author'] ? 'border-r' : ''}`}
                    >
                      {[
                        `${selectedVariant?.attributes?.['publicationYear']}`,
                        selectedVariant?.attributes?.['publicationMonth'] &&
                          selectedVariant?.attributes?.['publicationMonth'],
                        selectedVariant?.attributes?.['publicationDay'] &&
                          selectedVariant?.attributes?.['publicationDay'],
                      ]
                        .filter(Boolean)
                        .join('-')}
                    </Typography>
                  )}
                  {selectedVariant?.attributes?.['author'] && (
                    <Typography as="span" className="line-clamp-2 inline text-gray-600">
                      {selectedVariant?.attributes?.['author']}
                    </Typography>
                  )}
                </>
              )}
            </div>
          </Link>
          <div className="relative bottom-0 max-h-80 overflow-hidden py-6">
            {product?.description && (
              <div
                className="relative line-clamp-3 h-fit leading-[1.5em] text-gray-600"
                dangerouslySetInnerHTML={{ __html: product.description }}
              ></div>
            )}
          </div>
        </div>
        <div className="relative bottom-0 h-auto w-full">
          <div className="flex flex-col items-start justify-normal gap-8 py-6 text-blue-600">
            <Typography className="font-medium text-primary-black">Format</Typography>
            {product?.productTypeKey === 'physical-book' ? (
              <div className="flex items-center gap-6">
                <PhysicalBookIcon className="size-24" />
                <Typography>Printed Book</Typography>
              </div>
            ) : product?.productTypeKey === 'ebook' ? (
              <div className="flex items-center gap-6">
                <EbookIcon className="size-24" />
                <Typography>eBook</Typography>
              </div>
            ) : product?.productTypeKey === 'bundle' ? (
              <div className="flex items-center gap-6">
                <BundleIcon className="size-24" />
                <Typography>Book & eBook</Typography>
              </div>
            ) : (
              <div className="flex items-center gap-6">
                <Typography>
                  {product?.productTypeKey
                    ?.replace(/-/g, ' ')
                    .split(' ')
                    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
                    .join(' ')}
                </Typography>
              </div>
            )}
          </div>
          <div className="flex flex-col items-start justify-normal gap-8 py-6">
            <div className="flex flex-row gap-4">
              <Typography className="font-medium text-primary-black">Price</Typography>
              <Typography className="font-light text-gray-400">(starting at)</Typography>
            </div>
            <Prices price={selectedVariant?.price} discountedPrice={discountedPrice} />
          </div>
        </div>
      </div>
      <div className="flex gap-8 pt-20">
        <QuickAdd className="w-full" buttonIsVisible={true} product={product} onAddToCart={onAddToCart} />
      </div>
    </div>
  );
};

export default ProductTile;
